import React, { useEffect, useState } from 'react';
import { Hotel, Star, Home, Wallet, Lightbulb, ExternalLink, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';

const Accommodation = () => {
  const { language } = useLanguage();
  const content = weddingData[language].accommodation;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const getIcon = (iconName) => {
    const iconProps = { size: 28 };
    switch (iconName) {
      case 'star': return <Star {...iconProps} className="text-gold-500 fill-gold-500" />;
      case 'home': return <Home {...iconProps} className="text-gold-500" />;
      case 'wallet': return <Wallet {...iconProps} className="text-gold-500" />;
      default: return <Hotel {...iconProps} className="text-gold-500" />;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-ivory">
      {/* Header - Pastel Luxe harmonisé */}
      <section className="bg-gradient-to-br from-lilac-100 via-lilac-200 to-lilac-100 py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gold-200 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-lilac-200 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className={`container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Hotel className="mx-auto text-gold-500 mb-8" size={64} />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-playfair font-bold text-plum-900 mb-6">
            {content.title}
          </h1>
          <p className="text-xl text-plum-800 font-montserrat max-w-3xl mx-auto">
            {content.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold-500 to-transparent mx-auto rounded-full mt-8" />
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-ivory">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-plum-800 font-montserrat leading-relaxed">
              {content.intro}
            </p>
            <div className="divider-gold mt-8" />
          </div>
        </div>
      </section>

      {/* Hotel Categories */}
      <section className="py-20 bg-gradient-to-br from-lilac-50 via-ivory to-lilac-50">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-16">
            {content.categories.map((category, catIndex) => (
              <div
                key={catIndex}
                className={`transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${catIndex * 150}ms` }}
              >
                {/* Category Header */}
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-lilac-100 to-gold-50 rounded-2xl flex items-center justify-center shadow-soft">
                    {getIcon(category.icon)}
                  </div>
                  <h2 className="text-3xl font-playfair font-bold text-plum-900">
                    {category.name}
                  </h2>
                </div>

                {/* Hotels Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                  {category.hotels.map((hotel, hotelIndex) => (
                    <a
                      key={hotelIndex}
                      href={hotel.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-premium hover-lift block cursor-pointer group"
                    >
                      <h3 className="text-xl font-playfair font-bold text-plum-900 mb-2 group-hover:text-gold-600 transition-colors duration-300 flex items-center">
                        {hotel.name}
                        <svg className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </h3>
                      <p className="text-gold-600 font-montserrat font-semibold mb-2">
                        {hotel.area}
                      </p>
                      <p className="text-plum-800 font-montserrat font-bold text-lg mb-3">
                        {hotel.price}
                      </p>
                      <p className="text-plum-700 font-montserrat text-sm">
                        {hotel.features}
                      </p>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CosyCosy Alternative */}
      {content.alternative && (
        <section className="py-12 sm:py-16 bg-white">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto bg-gradient-to-br from-gold-50 to-lilac-50 rounded-2xl sm:rounded-3xl p-8 sm:p-10 border border-gold-100 text-center">
              <Search className="mx-auto text-gold-500 w-10 h-10 mb-4" />
              <h3 className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 mb-3">
                {content.alternative.title}
              </h3>
              <p className="text-plum-600 font-montserrat text-sm sm:text-base leading-relaxed mb-6">
                {content.alternative.description}
              </p>
              <a
                href={content.alternative.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 bg-gold-500 text-white font-montserrat text-sm font-semibold rounded-full hover:bg-gold-600 transition-colors duration-300"
                data-testid="cosycosy-link"
              >
                <ExternalLink size={16} />
                {content.alternative.linkText}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Tips Section */}
      <section className="py-20 bg-gradient-to-br from-lilac-100 via-lilac-200 to-lilac-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-40 h-40 bg-gold-200 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-lilac-100 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <Lightbulb className="text-gold-500" size={40} />
              <h2 className="text-3xl font-playfair font-bold text-plum-900">
                {content.tips.title}
              </h2>
            </div>
            <ul className="space-y-4">
              {content.tips.items.map((tip, index) => (
                <li key={index} className="flex items-start space-x-3 bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-lilac-200 shadow-soft">
                  <span className="text-gold-500 font-bold text-lg">•</span>
                  <p className="text-plum-800 font-montserrat">{tip}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accommodation;
